import {
  ABOUT_YOU_DISABLED_FIELD,
  DISABLED_FINANCIAL_REQUIRED_MAP_FIELD,
  SINGPASS_ABOUT_YOU_MAP_FIELD,
  SINGPASS_BUSINESS_ENTITY_MAP_FIELD,
} from '../const/singpass-field.const';
import { FormGroup } from '@angular/forms';
import { CorppassProfile, CPFContributionHistory, IMyInfo, NoticeOfAssessmentHistory } from '@core/singpass';
import { CompanyType } from '../type';
import { DeepPartial } from '@core/util';
import { compareDesc, format } from 'date-fns';
import * as moment from 'moment';
import { PersonnelGender, PersonnelMarital, PersonnelResidency } from '@core/data-access';

export function disableAboutYouSingPassFields(form: FormGroup) {
  disableFields(Object.keys(SINGPASS_ABOUT_YOU_MAP_FIELD), form);
}

export function disableAboutYouFields(form: FormGroup) {
  disableFields(Object.keys(ABOUT_YOU_DISABLED_FIELD), form);
}

export function disableBusinessEntitySingPassFields(form: FormGroup) {
  disableFields(Object.keys(SINGPASS_BUSINESS_ENTITY_MAP_FIELD), form);
}

export function disableFinancialRequiredFields(form: FormGroup) {
  disableFields(Object.keys(DISABLED_FINANCIAL_REQUIRED_MAP_FIELD), form);
}

function disableFields(keys: string[], form: FormGroup) {
  keys.forEach(key => form.get(key)?.disable({ emitEvent: false }));
}

export function mapDataFromSingPass(data: CorppassProfile): CorppassProfile {
  return mapCompanyTypeCode(data);
}

function mapCompanyTypeCode(data: CorppassProfile): CorppassProfile {
  const companyType = data.entity?.['basic-profile']?.['company-type'];
  if (companyType && companyType.code) {
    const mappedCode = getCompanyTypeCode(companyType.code);
    if (mappedCode) {
      companyType.code = mappedCode;
    }
  }

  return data;
}

function getCompanyTypeCode(code: string): CompanyType | undefined {
  const companyTypeMap: { [k in string]: CompanyType } = {
    A1: 'PUBLIC_COMPANY_LIMITED_BY_SHARES',
    A3: 'PUBLIC_COMPANY_LIMITED_BY_GUARANTEE',
    A4: 'UNLIMITED_PUBLIC_COMPANY',
    B1: 'PRIVATE_COMPANY_LIMITED_BY_SHARES',
    B2: 'EXEMPT_PRIVATE_COMPANY_LIMITED_BY_SHARES',
    B3: 'UNLIMITED_PRIVATE_COMPANY',
    B4: 'UNLIMITED_EXEMPT_PRIVATE_COMPANY',
    F1: 'FOREIGN_COMPANY_REGISTERED_IN_SINGAPORE',
  };
  return companyTypeMap[code];
}

export function mapGender(code: string): PersonnelGender | undefined {
  const genderMap: { [k in string]: PersonnelGender } = {
    M: PersonnelGender.MALE,
    F: PersonnelGender.FEMALE,
    U: PersonnelGender.UNKNOWN,
  };
  return genderMap[code];
}

export function mapResidentialStatus(code: unknown): PersonnelResidency | undefined {
  if (typeof code !== 'string') return undefined;

  const residentialStatusMap: { [k in string]: PersonnelResidency } = {
    A: PersonnelResidency.ALIEN,
    C: PersonnelResidency.CITIZEN,
    P: PersonnelResidency.PERMANENT_RESIDENT,
    U: PersonnelResidency.UNKNOWN,
    N: PersonnelResidency.NOT_APPLICABLE,
  };
  return residentialStatusMap[code];
}

export function mapMaritalStatus(code: string): PersonnelMarital | undefined {
  const maritalMap: { [k in string]: PersonnelMarital } = {
    '1': PersonnelMarital.SINGLE,
    '2': PersonnelMarital.MARRIED,
    '3': PersonnelMarital.WIDOWED,
    '4': PersonnelMarital.DIVORCED,
  };
  return maritalMap[code];
}

/**
 * https://api.singpass.gov.sg/library/myinfo/business/implementation-display-guidelines > Data > 2. Employment Related CPF Contribution History
 * SINGPASS MYINFO API ALWAYS RETURN MAXIMUM 15 MONTHS OF CPF CONTRIBUTION HISTORY
 * THE CONTRIBUTION ENTRIES ARE TO BE SORTED BY "PAID ON" FOLLOWED BY "FOR MONTH", IN DESCENDING VALUES
 */
export function mapCpfContributionHistory(args: { myinfo: IMyInfo }): DeepPartial<CPFContributionHistory[]> | null {
  const cpf: DeepPartial<CPFContributionHistory[]> = Object.assign([], args.myinfo.cpfcontributions?.history);
  if (!cpf || !cpf.length) return null;

  const monthFormat = 'YYYY-MM';
  const dateFormat = 'YYYY-MM-DD';
  const dateRegex = /^\d{2} [A-Za-z]{3} \d{4}$/;

  return cpf
    .sort((a, b) => {
      const aDate = moment(a?.date?.value, dateFormat).toDate();
      const bDate = moment(b?.date?.value, dateFormat).toDate();
      return compareDesc(aDate, bDate);
    })
    .map(v => {
      if (v && v.month && v.month.value) {
        v = { ...v, month: { value: format(moment(v.month.value, monthFormat).toDate(), 'MMM yyyy') } };
      }
      if (v && v.date && v.date.value && !dateRegex.test(v.date.value)) {
        v = { ...v, date: { value: format(moment(v.date.value, dateFormat).toDate(), 'dd MMM yyyy') } };
      }
      return v;
    });
}

/**
 * https://api.singpass.gov.sg/library/myinfo/business/implementation-display-guidelines > Data > 4. Notice of Assessment (Detailed)
 * SINGPASS MYINFO API ALWAYS RETURN LAST 2 YEARS OF NOTICE OF ASSESSMENT
 * THE NOTICE OF ASSESSMENT ENTRIES ARE TO BE SORTED BY "YEAR OF ASSESSMENT", IN DESCENDING VALUES
 */
export function mapNoticeOfAssessmentHistory(args: { myinfo: IMyInfo }): DeepPartial<NoticeOfAssessmentHistory[]> | null {
  const noa: DeepPartial<NoticeOfAssessmentHistory[]> = Object.assign([], args.myinfo.noahistory?.noas);
  if (!noa || !noa.length) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return noa.sort((a, b) => <any>b?.yearofassessment?.value - <any>a?.yearofassessment?.value);
}
